/**
 * Premium logo variants for VibeCheck — 5 takes on the wordmark.
 *
 * Visit /logo-preview to see them all side-by-side, then tell the agent which
 * variant ID to lock in (e.g. "ship option C"). Each variant exports the same
 * props as the master Logo.jsx so it's a drop-in swap.
 */
import { motion } from "framer-motion";

/* =========================================================================
 * Option A — Current italic wordmark (baseline)
 * ========================================================================= */
function OptionA({ size = 32 }) {
  return (
    <span
      className="inline-flex items-center leading-none whitespace-nowrap font-extrabold italic"
      style={{ fontFamily: "'Manrope', sans-serif", fontSize: size, letterSpacing: "-0.025em" }}
    >
      <span style={{ color: "#F4EAFF" }}>vibe</span>
      <motion.span
        className="relative inline-flex items-center justify-center"
        style={{ marginLeft: -2, marginRight: -2, width: size * 1.05, height: size * 1.05 }}
        animate={{ scale: [1, 1.07, 1] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      >
        <svg viewBox="0 0 24 24" width={size} height={size} style={{ filter: "drop-shadow(0 0 6px #FF2E9A) drop-shadow(0 0 14px #9B6BFF)" }}>
          <defs>
            <linearGradient id="lg-a-h" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%"  stopColor="#FF2E9A" />
              <stop offset="55%" stopColor="#E04AC0" />
              <stop offset="100%" stopColor="#9B6BFF" />
            </linearGradient>
          </defs>
          <path d="M12 21s-7-4.534-7-10a4.5 4.5 0 0 1 8-2.85A4.5 4.5 0 0 1 19 11c0 5.466-7 10-7 10z" fill="url(#lg-a-h)" />
        </svg>
      </motion.span>
      <span
        style={{
          backgroundImage: "linear-gradient(95deg, #FF2E9A 0%, #E04AC0 45%, #9B6BFF 100%)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
        }}
      >
        check
      </span>
    </span>
  );
}

/* =========================================================================
 * Option B — Editorial serif "VIBECHECK" with a heart replacing the V dot
 * Inspired by Vogue / luxury editorial covers. Big, confident, magazine.
 * ========================================================================= */
function OptionB({ size = 36 }) {
  return (
    <span
      className="inline-flex items-baseline leading-none whitespace-nowrap"
      style={{ fontFamily: "'Playfair Display', 'Cormorant Garamond', serif", fontSize: size, fontWeight: 900, letterSpacing: "-0.02em" }}
    >
      <span style={{ color: "#F4EAFF", fontStyle: "italic" }}>Vibe</span>
      <motion.span
        className="relative inline-flex items-baseline mx-[2px]"
        animate={{ rotate: [-3, 3, -3] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        style={{ alignSelf: "center", marginBottom: size * 0.02 }}
      >
        <svg viewBox="0 0 24 24" width={size * 0.7} height={size * 0.7} style={{ filter: "drop-shadow(0 0 8px #FF2E9A)" }}>
          <defs>
            <linearGradient id="lg-b-h" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FF2E9A" /><stop offset="100%" stopColor="#9B6BFF" />
            </linearGradient>
          </defs>
          <path d="M12 21s-7-4.534-7-10a4.5 4.5 0 0 1 8-2.85A4.5 4.5 0 0 1 19 11c0 5.466-7 10-7 10z" fill="url(#lg-b-h)" />
        </svg>
      </motion.span>
      <span
        style={{
          fontStyle: "italic",
          backgroundImage: "linear-gradient(95deg, #FF7BC0 0%, #C49BFF 100%)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
        }}
      >
        Check
      </span>
    </span>
  );
}

/* =========================================================================
 * Option C — Monogram lockup: glowing "VC" inside a pill, tagline "VIBECHECK"
 * Premium SaaS / Apple-product aesthetic. Most "logo-ish" of the bunch.
 * ========================================================================= */
function OptionC({ size = 40 }) {
  const pillH = size;
  return (
    <span className="inline-flex items-center gap-3" style={{ height: pillH }}>
      <span
        className="relative inline-flex items-center justify-center font-black"
        style={{
          width: pillH * 1.55,
          height: pillH,
          borderRadius: pillH / 2,
          background: "linear-gradient(135deg, #FF2E9A 0%, #9B6BFF 100%)",
          color: "#fff",
          fontFamily: "'Manrope', sans-serif",
          fontSize: pillH * 0.42,
          letterSpacing: "-0.04em",
          boxShadow: "0 0 24px rgba(255,46,154,0.55), 0 0 50px rgba(155,107,255,0.35), inset 0 1px 0 rgba(255,255,255,0.25)",
        }}
      >
        <span style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.45))" }}>VC</span>
        {/* subtle inner sheen */}
        <span
          className="absolute inset-0 pointer-events-none"
          style={{
            borderRadius: "inherit",
            background: "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, transparent 35%)",
          }}
          aria-hidden
        />
      </span>
      <span
        className="font-extrabold uppercase"
        style={{
          fontFamily: "'Manrope', sans-serif",
          fontSize: pillH * 0.45,
          color: "#F4EAFF",
          letterSpacing: "0.18em",
          textShadow: "0 0 18px rgba(196,155,255,0.25)",
        }}
      >
        vibecheck
      </span>
    </span>
  );
}

/* =========================================================================
 * Option D — Glow-tube neon sign "vibecheck" with the heart as a glyph
 * Bar-neon vibe. Cinematic, after-dark, premium nightlife energy.
 * ========================================================================= */
function OptionD({ size = 36 }) {
  return (
    <span
      className="inline-flex items-center leading-none whitespace-nowrap"
      style={{ fontFamily: "'Sora', 'Manrope', sans-serif", fontSize: size, fontWeight: 800 }}
    >
      <span
        style={{
          color: "transparent",
          WebkitTextStroke: "1.4px #FF2E9A",
          textShadow: "0 0 10px #FF2E9A, 0 0 22px #FF2E9A, 0 0 40px rgba(255,46,154,0.55)",
          letterSpacing: "-0.01em",
        }}
      >
        vibe
      </span>
      <motion.span
        className="inline-flex items-center justify-center mx-[3px]"
        animate={{ filter: ["drop-shadow(0 0 10px #FF2E9A)", "drop-shadow(0 0 22px #FF2E9A)", "drop-shadow(0 0 10px #FF2E9A)"] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
      >
        <svg viewBox="0 0 24 24" width={size * 0.78} height={size * 0.78}>
          <path
            d="M12 21s-7-4.534-7-10a4.5 4.5 0 0 1 8-2.85A4.5 4.5 0 0 1 19 11c0 5.466-7 10-7 10z"
            fill="none"
            stroke="#FF2E9A"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
      </motion.span>
      <span
        style={{
          color: "transparent",
          WebkitTextStroke: "1.4px #9B6BFF",
          textShadow: "0 0 10px #9B6BFF, 0 0 22px #9B6BFF, 0 0 40px rgba(155,107,255,0.55)",
          letterSpacing: "-0.01em",
        }}
      >
        check
      </span>
    </span>
  );
}

/* =========================================================================
 * Option E — Heart-bracket lockup: [♥ vibecheck] inside a glassy pill
 * Tech-luxe + dating-app icon energy. Most app-store-shelf-ready.
 * ========================================================================= */
function OptionE({ size = 38 }) {
  const pillH = size;
  return (
    <span
      className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full"
      style={{
        background: "rgba(255, 46, 154, 0.08)",
        border: "1.5px solid rgba(255, 46, 154, 0.35)",
        boxShadow: "0 0 24px rgba(255, 46, 154, 0.25), inset 0 0 18px rgba(155, 107, 255, 0.12)",
        backdropFilter: "blur(8px)",
      }}
    >
      <motion.span
        className="inline-flex"
        animate={{ scale: [1, 1.12, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        <svg viewBox="0 0 24 24" width={pillH * 0.7} height={pillH * 0.7} style={{ filter: "drop-shadow(0 0 8px #FF2E9A)" }}>
          <defs>
            <linearGradient id="lg-e-h" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FF2E9A" /><stop offset="100%" stopColor="#9B6BFF" />
            </linearGradient>
          </defs>
          <path d="M12 21s-7-4.534-7-10a4.5 4.5 0 0 1 8-2.85A4.5 4.5 0 0 1 19 11c0 5.466-7 10-7 10z" fill="url(#lg-e-h)" />
        </svg>
      </motion.span>
      <span
        className="font-black tracking-tight"
        style={{
          fontFamily: "'Manrope', sans-serif",
          fontSize: pillH * 0.5,
          color: "#fff",
          letterSpacing: "-0.025em",
        }}
      >
        vibe<span style={{
          backgroundImage: "linear-gradient(95deg, #FF7BC0 0%, #C49BFF 100%)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
        }}>check</span>
      </span>
    </span>
  );
}

export default function LogoPreview() {
  const options = [
    { id: "A", title: "Current — italic wordmark", desc: "Hand-set italic wordmark with a gradient heart between vibe + check. Friendly, playful, current.", Component: OptionA },
    { id: "B", title: "Editorial serif", desc: "Vogue-style serif Vibe Check with a heart instead of an ampersand. Premium magazine energy.", Component: OptionB },
    { id: "C", title: "Monogram lockup", desc: "Glossy VC monogram pill + uppercase tagline. The most 'app-store-shelf-ready' option. Most flexible at small sizes.", Component: OptionC },
    { id: "D", title: "Neon-tube outline", desc: "Glow-stroke outline letters with a pulsing heart. After-dark / nightlife / spicy energy. Cinematic.", Component: OptionD },
    { id: "E", title: "Glassy pill lockup", desc: "Heart + wordmark wrapped in a glass pill chip. Tech-luxe, modern dating-app aesthetic.", Component: OptionE },
  ];

  return (
    <div className="vc-bg min-h-screen px-6 md:px-12 py-12 text-white" style={{ background: "#0a060f" }}>
      <div className="max-w-5xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-extrabold mb-2 italic" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Logo options
        </h1>
        <p className="text-[#9090A8] mb-12 text-sm">
          Tell me which letter (A–E) and I'll lock it in across the app, splash, and favicon.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {options.map(({ id, title, desc, Component }) => (
            <div
              key={id}
              className="rounded-2xl p-7 md:p-9 relative"
              style={{
                background: "linear-gradient(135deg, rgba(20, 14, 26, 0.92), rgba(8, 4, 14, 0.92))",
                border: "1px solid rgba(255, 46, 154, 0.18)",
                boxShadow: "0 12px 38px rgba(0, 0, 0, 0.5)",
              }}
              data-testid={`logo-option-${id}`}
            >
              <span
                className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center font-black text-sm"
                style={{ background: "rgba(255, 46, 154, 0.15)", border: "1.5px solid rgba(255, 46, 154, 0.5)", color: "#FF7BC0" }}
              >
                {id}
              </span>

              <div className="flex items-center justify-center min-h-[90px] mb-6">
                <Component />
              </div>

              <div className="text-xs font-black tracking-[0.22em] uppercase mb-1" style={{ color: "#FF7BC0" }}>
                Option {id}
              </div>
              <div className="text-white font-bold text-lg mb-1">{title}</div>
              <div className="text-[#9090A8] text-sm leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
